import { useState, useCallback } from "react";
import Cropper, { type Area } from "react-easy-crop";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut } from "lucide-react";

interface Props {
  open: boolean;
  imageSrc: string | null;
  onCancel: () => void;
  onConfirm: (file: File) => void | Promise<void>;
  saving?: boolean;
}

async function cropToFile(src: string, area: Area): Promise<File> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const size = Math.min(1024, Math.round(area.width));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.92));
  if (!blob) throw new Error("Could not crop image");
  return new File([blob], `avatar-${Date.now()}.jpg`, { type: "image/jpeg" });
}

/** Lets members drag and zoom their photo so their face is centred before saving. */
export const PhotoCropDialog = ({ open, imageSrc, onCancel, onConfirm, saving }: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_: Area, px: Area) => setArea(px), []);

  const confirm = async () => {
    if (!imageSrc || !area) return;
    setBusy(true);
    try {
      await onConfirm(await cropToFile(imageSrc, area));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && !saving && onCancel()}>
      <DialogContent className="sm:max-w-md z-[130] max-h-[95dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adjust your photo</DialogTitle>
          <DialogDescription>Drag to move and zoom so your face sits in the middle.</DialogDescription>
        </DialogHeader>
        <div className="relative h-72 w-full overflow-hidden rounded-xl bg-muted">
          {imageSrc && (
            <Cropper image={imageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} />
          )}
        </div>
        <div className="flex items-center gap-3">
          <ZoomOut className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} aria-label="Zoom" />
          <ZoomIn className="h-4 w-4 text-muted-foreground" aria-hidden />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onCancel} disabled={busy || saving}>Cancel</Button>
          <Button onClick={confirm} disabled={!area || busy || saving}>{busy || saving ? "Saving…" : "Save photo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
